import { Bill } from '@/lib/types';
import { formatAUD } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Lightbulb, ExternalLink, TrendingDown } from 'lucide-react';
import { differenceInDays } from 'date-fns';

interface NegotiationTip {
  keywords: string[];
  category: string;
  tips: string[];
  compareLinks: { label: string; url: string }[];
}

const NEGOTIATION_TIPS: NegotiationTip[] = [
  {
    keywords: ['electricity', 'electric', 'power', 'energy'],
    category: 'Electricity',
    tips: [
      'Use Energy Made Easy (govt) to compare plans in your area.',
      'Ask your retailer for a "retention offer" — most have unadvertised discounts.',
      'Check if your plan has solar feed-in credits if you have panels.',
      'Consider switching to a time-of-use tariff if you can shift usage off-peak.',
    ],
    compareLinks: [
      { label: 'Energy Made Easy', url: 'https://www.energymadeeasy.gov.au' },
      { label: 'Victorian Energy Compare', url: 'https://compare.energy.vic.gov.au' },
    ],
  },
  {
    keywords: ['gas', 'natural gas'],
    category: 'Gas',
    tips: [
      'Bundle gas with electricity for a multi-service discount.',
      'Ask about pay-on-time discounts — some retailers offer 5-15% off.',
      'Compare plans on Energy Made Easy for your postcode.',
    ],
    compareLinks: [
      { label: 'Energy Made Easy', url: 'https://www.energymadeeasy.gov.au' },
    ],
  },
  {
    keywords: ['insurance', 'car insurance', 'home insurance', 'contents', 'health insurance', 'life insurance'],
    category: 'Insurance',
    tips: [
      'Get 3 quotes before renewing — loyalty rarely pays with insurers.',
      'Increase your excess to lower premiums (but keep it affordable).',
      'Ask about bundling home & contents, or car & home policies.',
      'Check if your super fund includes life/TPD cover before buying separate.',
      'For health insurance, compare on privatehealth.gov.au before April premium rises.',
    ],
    compareLinks: [
      { label: 'comparethemarket.com.au', url: 'https://www.comparethemarket.com.au' },
      { label: 'iSelect', url: 'https://www.iselect.com.au' },
      { label: 'Private Health (Govt)', url: 'https://www.privatehealth.gov.au' },
    ],
  },
  {
    keywords: ['internet', 'broadband', 'nbn', 'wifi'],
    category: 'Internet / NBN',
    tips: [
      'Check if your NBN speed tier matches your actual usage — downgrading saves $10-20/mo.',
      'Many providers offer new-customer deals; call to ask for the same rate.',
      'Whistleout and WhistleOut compare all Australian NBN plans by speed tier.',
    ],
    compareLinks: [
      { label: 'WhistleOut', url: 'https://www.whistleout.com.au/Broadband' },
    ],
  },
  {
    keywords: ['phone', 'mobile', 'telco', 'sim'],
    category: 'Mobile Phone',
    tips: [
      'BYO phone plans are almost always cheaper than contract plans.',
      'MVNOs (Boost, Woolworths Mobile, Aldi Mobile) use the same networks for less.',
      'Check if your current plan has more data than you use — downgrade to save.',
    ],
    compareLinks: [
      { label: 'WhistleOut Mobile', url: 'https://www.whistleout.com.au/MobilePhones' },
    ],
  },
  {
    keywords: ['rego', 'registration'],
    category: 'Vehicle Registration',
    tips: [
      'Pay yearly instead of quarterly to avoid instalment fees.',
      'Check if you qualify for a concession (pensioner, health care card).',
      'Review your CTP insurer — prices vary between providers in NSW/QLD.',
    ],
    compareLinks: [
      { label: 'State CTP Comparison (NSW)', url: 'https://www.greenslips.com.au' },
    ],
  },
  {
    keywords: ['water'],
    category: 'Water',
    tips: [
      'Water is usually a fixed-provider service, but check for hardship programs.',
      'Install water-efficient fixtures to reduce usage charges.',
    ],
    compareLinks: [],
  },
  {
    keywords: ['subscription', 'streaming', 'netflix', 'spotify', 'disney'],
    category: 'Subscriptions',
    tips: [
      "Audit all streaming services — cancel any you haven't used in 30 days.",
      'Share family plans with household members to split costs.',
      'Look for annual plan discounts (often 15-20% cheaper than monthly).',
    ],
    compareLinks: [],
  },
];

function matchTip(billName: string): NegotiationTip | null {
  const lower = billName.toLowerCase();
  return NEGOTIATION_TIPS.find(tip =>
    tip.keywords.some(kw => lower.includes(kw))
  ) || null;
}

interface Props {
  bills: Bill[];
}

export function BillNegotiationReminders({ bills }: Props) {
  const now = new Date();
  const REMINDER_WINDOW_DAYS = 30;

  const upcomingRenewals = bills
    .filter(b => !b.paid)
    .map(bill => {
      const due = new Date(bill.dueDate);
      const daysUntil = differenceInDays(due, now);
      const tip = matchTip(bill.name);
      return { bill, daysUntil, tip };
    })
    .filter(r => r.daysUntil <= REMINDER_WINDOW_DAYS && r.daysUntil >= -7 && r.tip)
    .sort((a, b) => a.daysUntil - b.daysUntil);

  if (upcomingRenewals.length === 0) return null;

  return (
    <div className="mb-4 space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-primary">
        <TrendingDown className="h-4 w-4" />
        <span>Negotiation Reminders</span>
      </div>
      {upcomingRenewals.map(({ bill, daysUntil, tip }) => (
        <Card key={bill.id} className="border-primary/20 bg-primary/5">
          <CardContent className="p-3">
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="text-sm font-medium">{bill.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {tip!.category} · {formatAUD(bill.amount)} · {bill.frequency}
                  </p>
                </div>
              </div>
              <Badge variant={daysUntil <= 7 ? 'destructive' : 'secondary'} className="shrink-0 text-[10px]">
                {daysUntil < 0 ? `${Math.abs(daysUntil)}d overdue` : daysUntil === 0 ? 'Due today' : `${daysUntil}d left`}
              </Badge>
            </div>

            <ul className="mb-2 space-y-1 pl-6">
              {tip!.tips.slice(0, 3).map((t, i) => (
                <li key={i} className="text-xs text-muted-foreground list-disc">{t}</li>
              ))}
            </ul>

            {tip!.compareLinks.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {tip!.compareLinks.map((link, i) => (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-md bg-background px-2 py-0.5 text-[10px] font-medium text-primary ring-1 ring-primary/20 hover:bg-primary/10 transition-colors"
                  >
                    {link.label}
                    <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
